/**
 * The `errorCode` the backend's maintenance middleware puts in its 503 body. Mirrors
 * `ErrorCodes.PlatformUnderMaintenance` on the API, and `maintenanceErrorCode` in the mobile app.
 */
export const MAINTENANCE_ERROR_CODE = 'PLATFORM_UNDER_MAINTENANCE';

/**
 * The anonymous status endpoint, relative to the API base URL.
 *
 * `/v1` because the API base configured for this app stops at `/api`, while the status endpoint
 * is versioned. Deliberately not `/ping`: that path is exempt from the maintenance middleware —
 * it has to be, or the deployment slot could never swap — so it answers 200 during maintenance
 * and would report a closed platform as a healthy one.
 */
export const APP_STATUS_PATH = '/v1/app-version';

/**
 * Why the app is not usable, when it is not usable.
 *
 * `unreachable` and `offline` differ only in how much we can prove. The browser reporting itself
 * offline is something we can establish, so that screen may name the cause; anything else leaves
 * it genuinely unknown — our own API being down looks identical to an office wifi that associates
 * and routes nowhere — and the copy there blames nobody.
 */
export type ServiceAvailabilityKind = 'available' | 'maintenance' | 'unreachable' | 'offline';

export interface ServiceAvailability {
  readonly kind: ServiceAvailabilityKind;

  /**
   * When maintenance is expected to end. Display only — nothing unblocks because it passed, and
   * it is routinely null.
   */
  readonly endsAt: Date | null;
}

export const AVAILABLE: ServiceAvailability = { kind: 'available', endsAt: null };
export const UNREACHABLE: ServiceAvailability = { kind: 'unreachable', endsAt: null };
export const OFFLINE: ServiceAvailability = { kind: 'offline', endsAt: null };

/**
 * Whether the browser is certain it has no network.
 *
 * Only the negative answer is worth anything. `navigator.onLine === true` means "an interface is
 * up", which a captive portal satisfies happily, so it is never treated as evidence that the
 * problem is ours. Guarded because the property is absent in some non-browser contexts, where
 * "we could not tell" must read as "not offline" rather than throwing.
 */
export function isBrowserOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

export function blocksApp(state: ServiceAvailability): boolean {
  return state.kind !== 'available';
}

/**
 * Reads the maintenance half of a status response, or of a 503 body.
 *
 * Absent, malformed or false all mean "open". A block is only ever raised on the field being
 * present and explicitly true, because every other reading of an ambiguous response locks an app
 * that is working.
 */
export function maintenanceFrom(body: unknown): ServiceAvailability {
  if (!isRecord(body) || body['maintenanceMode'] !== true) return AVAILABLE;

  return { kind: 'maintenance', endsAt: parseEndsAt(body['maintenanceEndsAt']) };
}

/** Whether a 503 body is ours, rather than the host's own gateway error. */
export function isMaintenanceBody(body: unknown): boolean {
  return isRecord(body) && body['errorCode'] === MAINTENANCE_ERROR_CODE;
}

export function endsAtFrom(body: unknown): Date | null {
  return isRecord(body) ? parseEndsAt(body['maintenanceEndsAt']) : null;
}

/**
 * The estimate is optional and cosmetic, so anything unreadable is dropped rather than allowed to
 * throw out of an error handler.
 */
function parseEndsAt(raw: unknown): Date | null {
  if (typeof raw !== 'string' || raw.length === 0) return null;

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
